import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import QuestIcon from '../QuestIcon'
import { Circle } from 'rc-progress'
import useIsActivityCourse from 'hooks/useIsActivityCourse'
import { getPercentage } from 'utils/courses'
import {
  CompletedOverlay,
  ActiveDarkOverlay,
  ActiveLightOverlay,
  Checkmark,
  ProgressDiamond,
  AssessmentComplete,
  AssessmentEmpty,
  ExpandedItemWrapper,
  QuestItem,
  EmptyBlock,
  DashboardWrapper,
  Progress,
  ProgressLine,
  QuestIconClassWrapper,
  HasNotification
} from './styles'
import FlexAlignCenter from 'components/styled/FlexAlignCenter'
import ApprovedFeedbackMarker from './ApprovedFeedbackMarker'
import AwaitingFeedbackMarker from './AwaitingFeedbackMarker'
import KeepWorkingFeedbackMarker from './KeepWorkingFeedbackMarker'
import TargetDateIcon from './TargetDateIcon/TargetDateIcon'
import { myStudentsDisplayLevelView } from 'constants/index'
import TargetDate from './TargetDate'
import useIsMentor from '../../../hooks/useIsMentor'
import { useSelector } from 'react-redux'

const getProgress = quest => {
  let progress = getPercentage(
    quest.progress.activities_completed_count,
    quest.progress.quest_activities_count
  )

  // spec says 'If just started (0% complete) a minimal green 'dot' displays at the top.' - copied where used elsewhere
  if (quest.progress.activities_completed_count === 0) {
    progress = '1'
  }
  return progress
}

export const getTooltipTargetId = (quest, studentId) =>
  `${studentId ? `student-${studentId}` : `mentor`}-cs-${
    quest.published_compiled_quest_id
  }-level-${quest.level_id}-quest-${quest.id}`

const LevelQuest = ({
  quest,
  studentId,
  isUserEnrolled,
  isUserViewingOwn,
  handleQuestClick,
  skillsCheckEnabled,
  courseIsArtifactAssessmentEnabled,
  tooltipId, // external tooltip should use getContent, data-tip will be the quest id
  displayLevelView,
  courseSectionId,
  targetDate,
  shouldDisplayTargetDateIcon,
  shouldShowDate,
  dispatch,
  dashboard = false
}) => {
  const isMentor = useIsMentor()
  const [hovered, setHovered] = useState(false)

  const isComplete =
    quest.progress &&
    quest.progress.quest_activities_count ===
      quest.progress.activities_completed_count
  const progress = quest.progress ? getProgress(quest) : null
  const awaiting = quest.progress && quest.progress.awaiting_approval
  const isApproved =
    quest.progress?.current_activity_feedback_status === 'approved' &&
    !quest.progress?.completed_at

  const keepWorking =
    quest.progress?.current_activity_feedback_status === 'disapproved' &&
    !quest.progress?.completed_at

  const tooltipTargetId = getTooltipTargetId(quest, studentId)

  const displayNotification = isUserEnrolled && (
    (!isMentor && quest.progress?.unread_notifications_count > 0) ||
    (isMentor && quest.progress?.outstanding_mentor_notifications)
  )

  const isActivityCourseSection = useIsActivityCourse(courseSectionId)
  const isActivityCourse = courseSectionId
    ? isActivityCourseSection
    : quest.progress?.quest?.operational_type === 'activity_course_quest'

  const WrapperForMyCourses = () => (
    <QuestIconClassWrapper
      noProgress={!quest.progress}
      isComplete={isComplete}
      isActivityCourse={isActivityCourse}
      isMentorViewingOwn={isMentor && isUserViewingOwn}
      data-tip={quest.id}
      data-for={tooltipId}
      id={tooltipTargetId}
      onClick={() => handleQuestClick({ quest, studentId })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={hovered ? 'hovered' : ''}
    >
      {isComplete && (
        <React.Fragment>
          <Checkmark />
          <CompletedOverlay isActivityCourse={isActivityCourse} />
        </React.Fragment>
      )}
      {progress && !isComplete && !isActivityCourse && (
        <Progress className="progress">
          {progress !== '0' && (
            <Circle
              percent={progress}
              strokeWidth="4"
              strokeColor="#00BD7E"
              trailWidth="0"
            />
          )}
        </Progress>
      )}
      {displayNotification && <HasNotification />}
      <QuestIcon quest={quest} size={32} />
      {awaiting && (
        <Fragment>
          <AwaitingFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveLightOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {isApproved && (
        <Fragment>
          <ApprovedFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveDarkOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {keepWorking && (
        <Fragment>
          <KeepWorkingFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveDarkOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {isActivityCourse && !!quest.progress && !isComplete && (
        <ProgressLine />
      )}
    </QuestIconClassWrapper>
  )

  const WrapperForStudentDashboard = () => (
    <DashboardWrapper
      noProgress={!quest.progress}
      isComplete={isComplete}
      isActivityCourse={isActivityCourse}
      isMentorViewingOwn={isMentor && isUserViewingOwn}
      data-tip
      data-for={tooltipId}
      id={tooltipTargetId}
      onClick={() => handleQuestClick({ quest, studentId })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isComplete && (
        <React.Fragment>
          <Checkmark />
          <CompletedOverlay isActivityCourse={isActivityCourse} />
        </React.Fragment>
      )}
      {progress && !isComplete && !isActivityCourse && (
        <Progress className="progress">
          {progress !== '0' && (
            <Circle
              percent={progress}
              strokeWidth="4"
              strokeColor="#00BD7E"
              trailWidth="0"
            />
          )}
        </Progress>
      )}
      {displayNotification && <HasNotification />}
      <QuestIcon quest={quest} size={32} />
      {awaiting && (
        <Fragment>
          <AwaitingFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveLightOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {isApproved && (
        <Fragment>
          <ApprovedFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveDarkOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {keepWorking && (
        <Fragment>
          <KeepWorkingFeedbackMarker isActivityCourse={isActivityCourse} />
          <ActiveDarkOverlay isActivityCourse={isActivityCourse} />
        </Fragment>
      )}

      {isActivityCourse && !!quest.progress && !isComplete && (
        <ProgressLine />
      )}
    </DashboardWrapper>
  )

  return (
    <FlexAlignCenter>
      <QuestItem>

        {dashboard
          ? WrapperForStudentDashboard()
          : WrapperForMyCourses()
        }

        {shouldDisplayTargetDateIcon &&
        targetDate &&
        !isComplete ? (
          <TargetDateIcon
            targetDate={targetDate}
            shouldShowDate={shouldShowDate}
          />
        ) : shouldDisplayTargetDateIcon ? (
          <EmptyBlock />
        ) : (
          ''
        )}
      </QuestItem>

      {displayLevelView === myStudentsDisplayLevelView.assessments && (
        <FlexAlignCenter
          onClick={() => handleQuestClick({ quest, studentId })}
          style={{
            justifyContent: 'space-between',
            padding: '0 12px 0 9px',
            cursor: quest.progress ? 'pointer' : 'default',
            pointerEvents: quest.progress ? 'all' : 'none',
            maxWidth: quest.noQuestProgressForAllStudents ? '20px' : 'unset'
          }}
        >
          {courseIsArtifactAssessmentEnabled && (
            <ExpandedItemWrapper style={{ minWidth: '28px' }}>
              {quest.progress &&
                quest.progress.quest_requires_artifact_assessment && (
                  <div style={{ marginRight: '8px' }}>
                    {quest.progress &&
                    quest.progress.last_artifact_assessment_at ? (
                      <AssessmentComplete>M</AssessmentComplete>
                    ) : (
                      <AssessmentEmpty>M</AssessmentEmpty>
                    )}
                  </div>
                )}
            </ExpandedItemWrapper>
          )}

          {skillsCheckEnabled && (
            <FlexAlignCenter style={{ minWidth: '55px' }}>
              <ExpandedItemWrapper>
                {quest.progress && (
                  <ProgressDiamond
                    hasProgress={
                      quest.progress &&
                      quest.progress.last_assessor_skill_assessment_at
                    }
                  >
                    <div style={{ transform: 'rotate(45deg)' }}>M</div>
                  </ProgressDiamond>
                )}
              </ExpandedItemWrapper>
              {quest.progress && (
                <div
                  style={{
                    width: '1px',
                    height: '15px',
                    background: '#dedfe0',
                    margin: '0 8px 0 9px'
                  }}
                />
              )}
              <ExpandedItemWrapper>
                {quest.progress && (
                  <ProgressDiamond
                    hasProgress={
                      quest.progress && quest.progress.last_skill_assessment_at
                    }
                  >
                    <div style={{ transform: 'rotate(45deg)' }}>S</div>
                  </ProgressDiamond>
                )}
              </ExpandedItemWrapper>
            </FlexAlignCenter>
          )}
        </FlexAlignCenter>
      )}
      {displayLevelView === myStudentsDisplayLevelView.targetDate && (
        <Fragment>
          <div
            style={{
              width: quest.allQuestCompletedForAllStudents ? '20px' : '90px'
            }}
          >
            {!(quest.progress && quest.progress.completed_at !== null) && (
              <TargetDate
                dispatch={dispatch}
                targetDate={targetDate}
                studentId={studentId}
                quest={quest}
                courseSectionId={courseSectionId}
              />
            )}
          </div>
        </Fragment>
      )}
    </FlexAlignCenter>
  )
}

LevelQuest.defaultProps = {
  isUserEnrolled: true
}

LevelQuest.propTypes = {
  dispatch: PropTypes.func,
  quest: PropTypes.object,
  studentId: PropTypes.number,
  handleQuestClick: PropTypes.func,
  levelIsExpanded: PropTypes.bool,
  skillsCheckEnabled: PropTypes.bool,
  courseIsArtifactAssessmentEnabled: PropTypes.bool,
  tooltipId: PropTypes.string,
  displayLevelView: PropTypes.string,
  targetDate: PropTypes.object,
  shouldDisplayTargetDateIcon: PropTypes.bool,
  shouldShowDate: PropTypes.bool,
  courseSectionId: PropTypes.number,
  dashboard: PropTypes.bool
}

export default LevelQuest
